import { useGlobalState } from '@/shared/store/global';
import type { CompletionContext } from '@codemirror/autocomplete';
import { autocompletion } from '@codemirror/autocomplete';
import { javascript, javascriptLanguage } from '@codemirror/lang-javascript';
import { sql } from '@codemirror/lang-sql';
import { HighlightStyle, StreamLanguage, syntaxHighlighting } from '@codemirror/language';
import { vb } from '@codemirror/legacy-modes/mode/vb';
import { Compartment, EditorState, RangeSetBuilder } from '@codemirror/state';
import type { ViewUpdate } from '@codemirror/view';
import { Decoration, EditorView, ViewPlugin } from '@codemirror/view';
import { tags } from '@lezer/highlight';
import { csharp } from '@replit/codemirror-lang-csharp';
import { basicSetup } from 'codemirror';
import { useEffect, useMemo, useRef } from 'react';
import { useDeepCompareEffect } from 'react-use';
import { useMemoizedFn } from '../hooks';
import { SCView } from '../view';
import { PROPERTY_NODE_TYPES, findCustomAttributesInCode } from './core';
import {
  csharpKeywords,
  javascriptKeywords,
  sqlKeywords,
  sqlKeywordsPink,
  vbKeywords,
} from './keyword';

const languageConf = new Compartment();
const autoCompleteConf = new Compartment();
const customHighlightConf = new Compartment();
const readOnlyConf = new Compartment();

const darkHighlightStyle = HighlightStyle.define([
  { tag: tags.keyword, color: '#c586c0' },
  { tag: [tags.name, tags.deleted, tags.character, tags.macroName], color: '#9cdcfe' },
  { tag: [tags.propertyName], color: '#9cdcfe' },
  { tag: [tags.function(tags.variableName), tags.labelName], color: '#dcdcaa' },
  { tag: [tags.color, tags.constant(tags.name), tags.standard(tags.name)], color: '#4fc1ff' },
  { tag: [tags.definition(tags.name), tags.separator], color: '#d4d4d4' },
  {
    tag: [
      tags.typeName,
      tags.className,
      tags.number,
      tags.changed,
      tags.annotation,
      tags.modifier,
      tags.self,
      tags.namespace,
    ],
    color: '#4ec9b0',
  },
  {
    tag: [
      tags.operator,
      tags.operatorKeyword,
      tags.url,
      tags.escape,
      tags.regexp,
      tags.link,
      tags.special(tags.string),
    ],
    color: '#d4d4d4',
  },
  { tag: [tags.meta, tags.comment], color: '#6a9955', fontStyle: 'italic' },
  { tag: tags.strong, fontWeight: 'bold' },
  { tag: tags.emphasis, fontStyle: 'italic' },
  { tag: tags.strikethrough, textDecoration: 'line-through' },
  { tag: tags.link, color: '#569cd6', textDecoration: 'underline' },
  { tag: tags.heading, fontWeight: 'bold', color: '#569cd6' },
  { tag: [tags.atom, tags.bool, tags.special(tags.variableName)], color: '#569cd6' },
  { tag: [tags.processingInstruction, tags.string, tags.inserted], color: '#ce9178' },
  { tag: tags.invalid, color: '#ff0000' },
]);

interface KeywordGroup {
  keywords: string[];
  colour: string;
  className?: string;
}

interface CommentStyle {
  colour: string;
  className?: string;
}

const createMultiKeywordTheme = (groups: KeywordGroup[], commentStyle?: CommentStyle) => {
  const styles: Record<string, any> = {};

  groups.forEach((group, index) => {
    const className = group.className || `cm-keyword-group-${index}`;
    styles[`.${className}`] = {
      color: group.colour,
    };
    styles[`.${className} *`] = {
      color: 'inherit !important',
    };
  });

  if (commentStyle) {
    const commentClass = commentStyle.className || 'cm-comment';
    styles[`.${commentClass}`] = {
      color: commentStyle.colour,
      fontStyle: '',
    };
    styles[`.${commentClass} *`] = {
      color: 'inherit !important',
    };
  }
  return EditorView.baseTheme(styles);
};

function createKeywordDecorations(groups: KeywordGroup[]) {
  return groups.map((group, index) =>
    Decoration.mark({
      class: group.className || `cm-keyword-group-${index}`,
      inclusive: true,
    }),
  );
}

function createCommentHighlighter(
  commentStyle: CommentStyle,
  language: keyof typeof PROPERTY_NODE_TYPES,
  stringStyle?: CommentStyle,
) {
  const commentMark = Decoration.mark({
    class: commentStyle.className || 'cm-comment',
    inclusive: true,
  });

  const stringMark = stringStyle
    ? Decoration.mark({ class: stringStyle.className || 'cm-sql-string', inclusive: true })
    : null;

  return ViewPlugin.fromClass(
    class {
      decorations: any;

      constructor(view: EditorView) {
        this.decorations = this.buildDecorations(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.buildDecorations(update.view);
        }
      }

      buildDecorations(view: EditorView) {
        const builder = new RangeSetBuilder<Decoration>();
        const matches: { from: number; to: number; markType: 'comment' | 'string' }[] = [];

        for (const { from, to } of view.visibleRanges) {
          const text = view.state.doc.sliceString(from, to);
          let match: RegExpExecArray | null;

          const lines = text.split('\n');
          let currentPos = from;
          if (language == 'javascript' || language == 'csharp') {
            const blockCommentRegex = /\/\*[\s\S]*?\*\//g;

            while ((match = blockCommentRegex.exec(text)) !== null) {
              const matchFrom = from + match.index;
              const matchTo = matchFrom + match[0].length;
              matches.push({ from: matchFrom, to: matchTo, markType: 'comment' });
            }

            for (const line of lines) {
              const lineCommentIndex = line.indexOf('//');
              if (lineCommentIndex !== -1) {
                const commentStart = currentPos + lineCommentIndex;
                const commentEnd = currentPos + line.length;
                matches.push({ from: commentStart, to: commentEnd, markType: 'comment' });
              }
              currentPos += line.length + 1; // +1 for newline
            }
          } else if (language == 'sql') {
            const blockCommentRegex = /\/\*[\s\S]*?\*\//g;

            while ((match = blockCommentRegex.exec(text)) !== null) {
              const matchFrom = from + match.index;
              const matchTo = matchFrom + match[0].length;
              matches.push({ from: matchFrom, to: matchTo, markType: 'comment' });
            }

            for (const line of lines) {
              const lineCommentIndex = line.indexOf('--');
              if (lineCommentIndex !== -1) {
                const commentStart = currentPos + lineCommentIndex;
                const commentEnd = currentPos + line.length;
                matches.push({ from: commentStart, to: commentEnd, markType: 'comment' });
              }
              currentPos += line.length + 1; // +1 for newline
            }

            const stringRegex = /'[^']*'/g;

            while ((match = stringRegex.exec(text)) !== null) {
              const matchFrom = from + match.index;
              const matchTo = matchFrom + match[0].length;
              matches.push({ from: matchFrom, to: matchTo, markType: 'string' });
            }
          } else if (language == 'vbscript') {
            for (const line of lines) {
              const lineCommentIndex = line.indexOf("'");
              if (lineCommentIndex !== -1) {
                const commentStart = currentPos + lineCommentIndex;
                const commentEnd = currentPos + line.length;
                matches.push({ from: commentStart, to: commentEnd, markType: 'comment' });
              }
              currentPos += line.length + 1; // +1 for newline
            }
          }
        }

        matches.sort((a, b) => a.from - b.from || a.to - b.to);

        const nonOverlapping: typeof matches = [];
        for (const match of matches) {
          if (
            nonOverlapping.length === 0 ||
            match.from >= nonOverlapping[nonOverlapping.length - 1].to
          ) {
            nonOverlapping.push(match);
          }
        }

        for (const match of nonOverlapping) {
          const mark = match.markType === 'string' && stringMark ? stringMark : commentMark;
          builder.add(match.from, match.to, mark);
        }

        return builder.finish();
      }
    },
    {
      decorations: (v) => v.decorations,
    },
  );
}

function createMultiGroupHighlighter(
  keywordGroups: KeywordGroup[],
  language: keyof typeof PROPERTY_NODE_TYPES,
) {
  const decorations = createKeywordDecorations(keywordGroups);

  return ViewPlugin.fromClass(
    class {
      decorations: any;

      constructor(view: EditorView) {
        this.decorations = this.buildDecorations(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.buildDecorations(update.view);
        }
      }

      buildDecorations(view: EditorView) {
        const builder = new RangeSetBuilder<Decoration>();

        if (!language || keywordGroups.length === 0) return builder.finish();
        const matches: { from: number; to: number; groupIndex: number }[] = [];

        keywordGroups.forEach((group, groupIndex) => {
          if (!group.keywords.length) return;

          const nodeTypes = PROPERTY_NODE_TYPES[language];
          let astMatches: { from: number; to: number }[] = [];

          if (nodeTypes) {
            for (const { from, to } of view.visibleRanges) {
              try {
                const astResults = findCustomAttributesInCode(
                  group.keywords,
                  language,
                  view.state,
                  from,
                  to,
                );
                astMatches = astMatches.concat(astResults);
              } catch (e) {
                console.log(e);
              }
            }
          }

          if (astMatches.length > 0) {
            astMatches.forEach((match) => {
              matches.push({ ...match, groupIndex });
            });
          } else {
            for (const { from, to } of view.visibleRanges) {
              const text = view.state.doc.sliceString(from, to);

              group.keywords.forEach((keyword) => {
                const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(`\\b${escapedKeyword}\\b`, 'gi');
                let match;

                while ((match = regex.exec(text)) !== null) {
                  const matchFrom = from + match.index;
                  const matchTo = matchFrom + match[0].length;
                  matches.push({ from: matchFrom, to: matchTo, groupIndex });
                }
              });
            }
          }
        });

        matches.sort((a, b) => a.from - b.from || a.to - b.to);

        const nonOverlapping: typeof matches = [];
        for (const match of matches) {
          if (
            nonOverlapping.length === 0 ||
            match.from >= nonOverlapping[nonOverlapping.length - 1].to
          ) {
            nonOverlapping.push(match);
          }
        }

        for (const match of nonOverlapping) {
          builder.add(match.from, match.to, decorations[match.groupIndex]);
        }

        return builder.finish();
      }
    },
    {
      decorations: (v) => v.decorations,
    },
  );
}

function onChangePlugin(callback: (state: EditorState) => void) {
  return EditorView.updateListener.of((update) => {
    if (update.docChanged) {
      callback(update.state);
    }
  });
}

interface SCCodeEditorProps {
  language: keyof typeof PROPERTY_NODE_TYPES;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  customAttributes?: string[];
  autoCompleteOptions?: {
    label: string;
    /** variable */
    type: string;
    info?: string;
  }[];
  maxHeight?: string | number;
}

const COMMENT_COLOUR = 'var(--sc-code-editor-comment)';
const SQL_STRING_COLOUR = 'var(--sc-code-editor-sql-string)';

const SCCodeEditor = ({
  language,
  value,
  onChange,
  disabled,
  autoCompleteOptions,
  maxHeight,
}: SCCodeEditorProps) => {
  const { userSession } = useGlobalState();
  const ref = useRef<HTMLDivElement>(null);
  const refEditor = useRef<EditorView | null>(null);
  const themeRef = useRef<any>(null);
  const commentHighlightConf = useRef(new Compartment());
  const currentValue = useRef(value);
  currentValue.current = value;
  const lastOnChangeValue = useRef(value);

  const isDark = useMemo(() => {
    if (userSession?.theme === 'dark') return true;
    if (userSession?.theme === 'light') return false;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  }, [userSession]);

  const handleChange = useMemoizedFn((v: string) => {
    if (v !== value) {
      lastOnChangeValue.current = v;
      onChange?.(v);
    }
  });

  const currentKeywords = useMemo(() => {
    switch (language) {
      case 'sql':
        return [
          { keywords: sqlKeywords, colour: 'var(--sc-code-editor-keywords-sql)' },
          { keywords: sqlKeywordsPink, colour: 'var(--sc-code-editor-keywords-sql-pink)' },
        ];
      case 'vbscript':
        return [{ keywords: vbKeywords, colour: 'var(--sc-code-editor-keywords-vb)' }];
      case 'csharp':
        return [{ keywords: csharpKeywords, colour: 'var(--sc-code-editor-keywords-csharp)' }];
      case 'javascript':
        return [{ keywords: javascriptKeywords, colour: 'var(--sc-code-editor-keywords-js)' }];
      default:
        return [];
    }
  }, [language]);

  const effectiveKeywordGroups = useRef<KeywordGroup[]>([]);

  const editorHeight = maxHeight
    ? typeof maxHeight === 'number'
      ? `${maxHeight}px`
      : maxHeight
    : undefined;

  const editorTheme = useMemo(
    () =>
      EditorView.theme(
        {
          '&': {
            backgroundColor: 'var(--sc-code-editor-bg)',
            color: 'var(--sc-code-editor-text)',
            ...(editorHeight ? { height: editorHeight } : {}),
          },
          '.cm-scroller': {
            overflow: editorHeight ? 'auto' : 'visible',
            minHeight: '0',
          },
          '.cm-content': {
            caretColor: 'var(--text-primary)',
          },
          '.cm-activeLine': {
            backgroundColor: 'var(--surface-base-selected)',
          },
          '.cm-activeLineGutter': {
            backgroundColor: 'var(--surface-base-selected)',
          },
          '.cm-gutters': {
            backgroundColor: 'var(--sc-code-editor-bg)',
            color: 'var(--text-tertiary)',
            borderRight: 'none',
          },
          '.cm-cursor, .cm-dropCursor': {
            borderLeftColor: 'var(--text-primary)',
          },
          '.cm-selectionBackground': {
            backgroundColor: 'var(--surface-base-selected) !important',
          },
          '.cm-panels': {
            backgroundColor: 'var(--sc-code-editor-bg)',
            color: 'var(--sc-code-editor-text)',
          },
          '.cm-tooltip': {
            backgroundColor: 'var(--surface-elevated-default, var(--sc-code-editor-bg))',
            color: 'var(--sc-code-editor-text)',
            border: '1px solid var(--border-base)',
          },
          '.cm-tooltip-autocomplete ul li[aria-selected]': {
            backgroundColor: 'var(--surface-base-selected)',
          },
        },
        { dark: isDark },
      ),
    [isDark, editorHeight],
  );

  useEffect(() => {
    effectiveKeywordGroups.current = currentKeywords;
    const commentStyle: CommentStyle = { colour: COMMENT_COLOUR };
    themeRef.current = createMultiKeywordTheme(effectiveKeywordGroups.current, commentStyle);

    const editor = new EditorView({
      doc: currentValue.current || '',
      extensions: [
        basicSetup,
        ...(isDark ? [syntaxHighlighting(darkHighlightStyle)] : []),
        languageConf.of([]),
        autoCompleteConf.of([]),
        customHighlightConf.of([]),
        commentHighlightConf.current.of([]),
        themeRef.current,
        readOnlyConf.of([]),
        editorTheme,
        onChangePlugin((state) => {
          handleChange(state.doc.toString());
        }),
        // After dropping text from the fragment panel, the browser selects the
        // inserted text.  Pressing any key (e.g. Space) then replaces the
        // selection, deleting the dropped content.  Collapse the selection to a
        // cursor after the drop so subsequent typing appends instead of replacing.
        EditorView.domEventHandlers({
          drop(_, view) {
            requestAnimationFrame(() => {
              const { head } = view.state.selection.main;
              view.dispatch({ selection: { anchor: head } });
            });
          },
        }),
      ],
      parent: ref.current!,
    });
    refEditor.current = editor;

    return () => {
      editor.destroy();
    };
  }, [isDark]);

  useDeepCompareEffect(() => {
    const editor = refEditor.current;
    if (!editor) return;

    effectiveKeywordGroups.current = currentKeywords;

    const commentStyle: CommentStyle = { colour: COMMENT_COLOUR };
    themeRef.current = createMultiKeywordTheme(effectiveKeywordGroups.current, commentStyle);

    function myCompletions(context: CompletionContext) {
      const word = context.matchBefore(/[\w.@]+/);
      if (!word) return null;
      if (word.from == word.to && !context.explicit) return null;

      let defaultOptions: any = [];
      switch (language) {
        case 'sql':
          defaultOptions = sqlKeywords.map((k) => ({ label: k, type: 'keyword' }));
          break;
        case 'vbscript':
          defaultOptions = vbKeywords.map((k) => ({ label: k, type: 'keyword' }));
          break;
        case 'csharp':
          defaultOptions = csharpKeywords.map((k) => ({ label: k, type: 'keyword' }));
          break;
        case 'javascript':
          defaultOptions = javascriptKeywords.map((k) => ({ label: k, type: 'keyword' }));
          break;
        default:
          return null;
      }
      return {
        from: word.from,
        options: [...(autoCompleteOptions || []), ...defaultOptions],
      };
    }

    const extensionMap = {
      javascript: [
        javascript(),
        javascriptLanguage.data.of({
          autocomplete: myCompletions,
        }),
      ],
      csharp: [
        csharp(),
        autocompletion({
          override: [myCompletions],
        }),
      ],
      vbscript: [StreamLanguage.define(vb)],
      sql: [
        sql(),
        autocompletion({
          override: [myCompletions],
        }),
      ],
    };

    editor.dispatch({
      effects: [
        languageConf.reconfigure(extensionMap[language]),
        customHighlightConf.reconfigure([
          createMultiGroupHighlighter(effectiveKeywordGroups.current, language),
        ]),
        commentHighlightConf.current.reconfigure([
          createCommentHighlighter(
            commentStyle,
            language,
            language === 'sql'
              ? { colour: SQL_STRING_COLOUR, className: 'cm-sql-string' }
              : undefined,
          ),
        ]),
        readOnlyConf.reconfigure([
          EditorView.editable.of(!disabled),
          EditorState.readOnly.of(Boolean(disabled)),
        ]),
      ],
    });

    const style = document.getElementById('cm-keyword-theme');
    if (style) {
      style.remove();
    }

    const newStyle = document.createElement('style');
    newStyle.id = 'cm-keyword-theme';
    let styleContent = '';

    effectiveKeywordGroups.current.forEach((group, index) => {
      const className = group.className || `cm-keyword-group-${index}`;
      styleContent += `.${className} { color: ${group.colour} !important; }\n`;
    });

    styleContent += `.cm-comment { color: ${COMMENT_COLOUR} !important; font-style: italic; }\n`;
    styleContent += `.cm-sql-string { color: ${SQL_STRING_COLOUR} !important; }\n`;

    newStyle.textContent = styleContent;
    document.head.appendChild(newStyle);
  }, [disabled, language, autoCompleteOptions, COMMENT_COLOUR, handleChange]);

  useEffect(() => {
    const editor = refEditor.current;
    if (editor) {
      const editorValue = editor.state.doc.toString();
      if (value !== editorValue) {
        // Only sync prop → editor if this is an external change (e.g. form reset),
        // not an echo of our own onChange. When value matches what we last sent,
        // the editor is already correct or ahead with newer user input.
        if (value !== lastOnChangeValue.current) {
          editor.dispatch({
            changes: {
              from: 0,
              to: editorValue.length,
              insert: value || '',
            },
          });
        }
      }
      lastOnChangeValue.current = value;
    }
  }, [value]);

  return <SCView ref={ref} wFull border />;
};

export { SCCodeEditor };
export type { CommentStyle, KeywordGroup, SCCodeEditorProps };
